import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { FacadeService } from 'src/app/shared/services/facade.service';
import { MembersSearchState } from '../models/members-search.models';
import * as selectors from '../selectors/members-search.selectors';

@Injectable({ providedIn: 'root' })
export class MembersSearchFacadeService extends FacadeService {
  constructor(public override store: Store<MembersSearchState>) {
    super(store);
  }

  // OBSERVABLES
  mainPanelExpanded$ = this.storeSelect(selectors.getMainPanelExpanded);
  loyaltyPanelExpanded$ = this.storeSelect(selectors.getLoyaltyPanelExpanded);
  personalPanelExpanded$ = this.storeSelect(selectors.getPersonalPanelExpanded);
  otherPanelExpanded$ = this.storeSelect(selectors.getOtherPanelExpanded);
  cardNumber$ = this.storeSelect(selectors.getCardNumber);
  schemaNameOptions$ = this.storeSelect(selectors.getSchemaNameOptions);
  schema$ = this.storeSelect(selectors.getSchema);
  registrationDateFrom$ = this.storeSelect(selectors.getRegistrationDateFrom);
  registrationDateUntil$ = this.storeSelect(selectors.getRegistrationDateUntil);
  registrationDateFromZ$ = this.storeSelect(selectors.getRegistrationDateFromZ);
  registrationDateUntilZ$ = this.storeSelect(selectors.getRegistrationDateUntilZ);
  registrationStoreOptions$ = this.storeSelect(selectors.getRegistrationStoreOptions);
  registrationStore$ = this.storeSelect(selectors.getRegistrationStore);
  registrationDateFromValidation$ = this.storeSelect(selectors.getRegistrationDateFromValidation);
  registrationDateUntilValidation$ = this.storeSelect(selectors.getRegistrationDateUntilValidation);
  registrationDateLTStartDate$ = this.storeSelect(selectors.getRegistrationDateLTStartDate);
  memberCategoryOptions$ = this.storeSelect(selectors.getMemberCategoryOptions);
  memberCategory$ = this.storeSelect(selectors.getMemberCategory);
  firstName$ = this.storeSelect(selectors.getFirstName);
  email$ = this.storeSelect(selectors.getEmail);
  lastName$ = this.storeSelect(selectors.getLastName);
  mobile$ = this.storeSelect(selectors.getMobile);
  countryOptions$ = this.storeSelect(selectors.getCountryOptions);
  country$ = this.storeSelect(selectors.getCountry);
  memberTagOptions$ = this.storeSelect(selectors.getMemberTagOptions);
  memberTag$ = this.storeSelect(selectors.getMemberTag);
  clubOptions$ = this.storeSelect(selectors.getClubOptions);
  club$ = this.storeSelect(selectors.getClub);
  qivosID$ = this.storeSelect(selectors.getQivosID);
  attributeTypeOptions$ = this.storeSelect(selectors.getAttributeTypeOptions);
  attributeType$ = this.storeSelect(selectors.getAttributeType);
  attributeNameOptions$ = this.storeSelect(selectors.getAttributeNameOptions);
  attributeName$ = this.storeSelect(selectors.getAttributeName);
  attributeValue$ = this.storeSelect(selectors.getAttributeValue);
  attributeBoolValue$ = this.storeSelect(selectors.getAttributeBoolValue);
  attributeDateFrom$ = this.storeSelect(selectors.getAttributeDateFrom);
  attributeDateUntil$ = this.storeSelect(selectors.getAttributeDateUntil);
  attributeDateFromZ$ = this.storeSelect(selectors.getAttributeDateFromZ);
  attributeDateUntilZ$ = this.storeSelect(selectors.getAttributeDateUntilZ);
  attributeDateFromValidation$ = this.storeSelect(selectors.getAttributeDateFromValidation);
  attributeDateUntilValidation$ = this.storeSelect(selectors.getAttributeDateUntilValidation);
  attributeDateLTStartDate$ = this.storeSelect(selectors.getAttributeDateLTStartDate);
  includeInactive$ = this.storeSelect(selectors.getIncludeInactive);
  disableSearch$ = this.storeSelect(selectors.getDisableSearch);
  attributeNameStringOrInteger$ = this.storeSelect(selectors.getAttributeNameStringOrInteger);
  attributeNameInteger$ = this.storeSelect(selectors.getAttributeNameInteger);
  attributeNameBoolean$ = this.storeSelect(selectors.getAttributeNameBoolean);
  attributeNameDate$ = this.storeSelect(selectors.getAttributeNameDate);
  editRole$ = this.storeSelect(selectors.getEditRole);

  // OBSERVABLE'S VALUES
  get mainPanelExpanded() {
    return this.storeSnapshot(this.mainPanelExpanded$);
  }

  get loyaltyPanelExpanded() {
    return this.storeSnapshot(this.loyaltyPanelExpanded$);
  }

  get personalPanelExpanded() {
    return this.storeSnapshot(this.personalPanelExpanded$);
  }

  get otherPanelExpanded() {
    return this.storeSnapshot(this.otherPanelExpanded$);
  }

  get cardNumber() {
    return this.storeSnapshot(this.cardNumber$);
  }

  get schemaNameOptions() {
    return this.storeSnapshot(this.schemaNameOptions$);
  }

  get schema() {
    return this.storeSnapshot(this.schema$);
  }

  get registrationDateFrom() {
    return this.storeSnapshot(this.registrationDateFrom$);
  }

  get registrationDateUntil() {
    return this.storeSnapshot(this.registrationDateUntil$);
  }

  get registrationStoreOptions() {
    return this.storeSnapshot(this.registrationStoreOptions$);
  }

  get registrationStore() {
    return this.storeSnapshot(this.registrationStore$);
  }

  get registrationDateFromValidation() {
    return this.storeSnapshot(this.registrationDateFromValidation$);
  }

  get registrationDateUntilValidation() {
    return this.storeSnapshot(this.registrationDateUntilValidation$);
  }

  get memberCategory() {
    return this.storeSnapshot(this.memberCategory$);
  }

  get firstName() {
    return this.storeSnapshot(this.firstName$);
  }

  get email() {
    return this.storeSnapshot(this.email$);
  }

  get lastName() {
    return this.storeSnapshot(this.lastName$);
  }

  get mobile() {
    return this.storeSnapshot(this.mobile$);
  }

  get countryOptions() {
    return this.storeSnapshot(this.countryOptions$);
  }

  get country() {
    return this.storeSnapshot(this.country$);
  }

  get memberTag() {
    return this.storeSnapshot(this.memberTag$);
  }

  get club() {
    return this.storeSnapshot(this.club$);
  }

  get qivosID() {
    return this.storeSnapshot(this.qivosID$);
  }

  get attributeType() {
    return this.storeSnapshot(this.attributeType$);
  }

  get attributeName() {
    return this.storeSnapshot(this.attributeName$);
  }

  get attributeValue() {
    return this.storeSnapshot(this.attributeValue$);
  }

  get attributeBoolValue() {
    return this.storeSnapshot(this.attributeBoolValue$);
  }

  get attributeDateFrom() {
    return this.storeSnapshot(this.attributeDateFrom$);
  }

  get attributeDateUntil() {
    return this.storeSnapshot(this.attributeDateUntil$);
  }

  get attributeDateFromValidation() {
    return this.storeSnapshot(this.attributeDateFromValidation$);
  }

  get attributeDateUntilValidation() {
    return this.storeSnapshot(this.attributeDateUntilValidation$);
  }

  get includeInactive() {
    return this.storeSnapshot(this.includeInactive$);
  }
}
